import {FormHelpers} from './components/form-helpers';
import {GeneralUtility} from './utilities/general-utility';
import {Captcha} from './components/captcha';
import GLightbox from 'glightbox';
import {CookieUtility} from './utilities/cookie-utility';
import Flickity from 'flickity';
import {Gallery} from './components/gallery';
import {Filter as EventFilter} from "./plugins/event/filter";
import {List as EventList} from "./plugins/event/list";
import {Picker as EventPicker} from "./plugins/event/picker";
import {List as ExpertiseList} from "./plugins/expertise/list";
import {Overview as ExpertiseOverview} from "./plugins/expertise/overview";
import {Filter as LawyerFilter} from "./plugins/lawyer/filter";
import {List as LawyerList} from "./plugins/lawyer/list";
import {Picker as LawyerPicker} from "./plugins/lawyer/picker";
import {Quotes as LawyerQuotes} from "./plugins/lawyer/quotes";
import {Filter as NewsFilter} from "./plugins/news/filter";
import {Latest as NewsLatest} from "./plugins/news/latest";
import {List as NewsList} from "./plugins/news/list";
import {Picker as NewsPicker} from "./plugins/news/picker";
import {Filter as PublicationFilter} from "./plugins/publication/filter";
import {List as PublicationList} from "./plugins/publication/list";
import {Picker as PublicationPicker} from "./plugins/publication/picker";

/**
 * Main entry point for the app.
 *
 * @author Philipp Seiler <sep@earlybird.at>
 */
export class Main {
  constructor() {
    // Initialize all non-vue components, plugins, libraries etc.
    this.initializeLightbox();
    this.initializeSearchForm();
    this.initializeScrollToButton();
    this.initializeContrastButton();
    this.initializeHero();
    this.initializeComponents();
    new FormHelpers();
    new Captcha();

    // Set some useful global identifiers
    if (GeneralUtility.isTouch()) {
      document.body.classList.add('is-touch');
    } else {
      document.body.classList.add('no-touch');
    }
    if (GeneralUtility.isReducedMotion()) {
      document.body.classList.add('is-reduced-motion');
    } else {
      document.body.classList.add('no-reduced-motion');
    }

    // Listen to scrolling
    this.fixViewHeight();
    GeneralUtility.browserEmitter.on('scroll', position => {
      this.fixViewHeight();
      this.adjustMainNavigationOnScrolling(position.previousScrollY);
    });

    // Listen to device orientation
    this.adjustDeviceOrientation(GeneralUtility.getDeviceOrientation());
    GeneralUtility.browserEmitter.on('orientation', orientation => {
      this.fixViewHeight();
      this.adjustDeviceOrientation(orientation);
    });
  }

  /**
   * Fixes a bug where mobile devices like iPad calculate viewheight-property wrongly.
   */
  private fixViewHeight(): void {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', vh + 'px');
  }

  /**
   * Adjusts the orientation.
   *
   * @param {string} orientation Device orientation
   */
  private adjustDeviceOrientation(orientation: string): void {
    if (orientation === 'landscape') {
      document.body.classList.add('landscape');
      document.body.classList.remove('portrait');
    } else {
      document.body.classList.remove('landscape');
      document.body.classList.add('portrait');
    }
  }

  /**
   * Initializes the lightbox.
   */
  private initializeLightbox(): void {
    GLightbox({
      selector: '*[data-lightbox]'
    });
  }

  /**
   * Adjusts the main navigation when scrolling.
   *
   * @param {int} previousScrollY Previous window.pageYOffset value
   */
  private adjustMainNavigationOnScrolling = function (previousScrollY: number): void {
    const header = document.querySelector('#header');
    if (!header) {
      return;
    }
    const headerHeight = header.clientHeight;
    const offset = window.innerHeight / 2;

    // Scrolling up
    if (previousScrollY > window.pageYOffset) {
      // Show header
      header.classList.add('header-out', 'header-in');
      // Remove header background color when scrolling near the top
      if (window.pageYOffset < offset) {
        header.classList.remove('header-fixed-bg');
      }
      // Reset classes when scrolled to top
      if (window.pageYOffset === 0) {
        header.classList.remove('header-fixed', 'header-out', 'header-in');
      }
    }

    // Scrolling down
    else {
      // Hide header when user scrolled passed it
      if (window.pageYOffset > headerHeight) {
        header.classList.add('header-fixed', 'header-fixed-bg');
        header.classList.remove('header-in');
      }
    }
  };

  /**
   * Initializes the global search form.
   */
  private initializeSearchForm(): void {
    const togglers = document.querySelectorAll<HTMLElement>('.btn-form-search-toggle');
    for (const toggler of togglers) {
      toggler.addEventListener('click', (e) => {
        e.preventDefault();

        toggler.classList.toggle('btn-form-search-active');
        const nextElement = toggler.nextElementSibling;
        if (!nextElement) {
          return;
        }
        nextElement.classList.toggle('form-search-open');

        // Focus input element
        nextElement.querySelector<HTMLElement>('.input-search')?.focus();

        // Close button within the search form
        nextElement.querySelector('.btn-form-search-close')?.addEventListener('click', buttonEvent => {
          buttonEvent.preventDefault();
          nextElement.classList.remove('form-search-open');
          toggler.classList.remove('btn-form-search-active');
        });
      });
    }
  }

  /**
   * Initializes the scroll-to button.
   */
  private initializeScrollToButton(): void {
    const scrollToButtons = document.querySelectorAll<HTMLElement>('[data-scroll-to]');
    for (const button of scrollToButtons) {
      if (!button.dataset.scrollTo) {
        return;
      }
      const target = document.querySelector(button.dataset.scrollTo);
      if (!target) {
        return;
      }
      button.addEventListener('click', e => {
        e.preventDefault();
        target.scrollIntoView({
          'block': 'start',
          'behavior': 'smooth'
        });
      });
    }
  }

  /**
   * Initializes the contrast button.
   */
  private initializeContrastButton(): void {
    let contrastActive = CookieUtility.get('contrast') === '1';
    const buttons = document.querySelectorAll<HTMLElement>('.btn-contrast');
    for (const button of buttons) {
      if (contrastActive) {
        button.classList.add('active');
      }
      button.addEventListener('click', () => {
        // Disable contrast
        if (contrastActive) {
          CookieUtility.delete('contrast');
          document.body.classList.remove('contrast');
          button.classList.remove('active');
        }
        // Enable contrast
        else {
          CookieUtility.set('contrast', '1');
          document.body.classList.add('contrast');
          button.classList.add('active');
        }
        contrastActive = !contrastActive;
      });
    }
  }

  /**
   * Initializes the page hero section.
   */
  private initializeHero(): void {
    const heroContainer = document.querySelector('.hero');
    if (!heroContainer) {
      return;
    }

    // Add custom class to main headers, if they contain lots of characters
    const headlines = heroContainer.querySelectorAll<HTMLElement>('h1, .display-1, h2, .display-2, h3, .display-3, h4, .display-4, h5, .display-5, h6, .display-6');
    headlines.forEach(headline => {
      if (!headline.textContent) {
        return;
      }
      const text = headline.textContent.trim();
      // Add breakpoint classes
      for (let i = 0; i <= text.length; i++) {
        if (i > 0 && i % 10 === 0) {
          headline.classList.add('characters-gte-' + i);
        }
      }
    });

    // Initialize Flickity slider, if enabled
    const sliderContainer = heroContainer.querySelector('.hero .slider-container');
    if (sliderContainer) {
      new Flickity(sliderContainer, {
        draggable: true,
        freeScroll: false,
        wrapAround: true,
        autoPlay: false,
        adaptiveHeight: true,
        lazyLoad: false,
        cellSelector: '.cell',
        cellAlign: 'left',
        pageDots: false
      });
    }
  }

  /**
   * Initializes components.
   */
  private initializeComponents(): void {
    // Content elements
    for (const container of document.querySelectorAll<HTMLElement>('[data-ctype="sitepackage_gallery"]')) {
      new Gallery(container);
    }

    // Event
    for (const container of document.querySelectorAll<HTMLElement>('[data-ctype="sitepackage_eventfilter"]')) {
      new EventFilter(container);
    }
    for (const container of document.querySelectorAll<HTMLElement>('[data-ctype="sitepackage_eventlist"]')) {
      new EventList(container);
    }
    for (const container of document.querySelectorAll<HTMLElement>('[data-ctype="sitepackage_eventpicker"]')) {
      new EventPicker(container);
    }

    // Expertise
    for (const container of document.querySelectorAll<HTMLElement>('[data-ctype="sitepackage_expertiselist"]')) {
      new ExpertiseList(container);
    }
    for (const container of document.querySelectorAll<HTMLElement>('[data-ctype="sitepackage_expertiseoverview"]')) {
      new ExpertiseOverview(container);
    }

    // Lawyer
    for (const container of document.querySelectorAll<HTMLElement>('[data-ctype="sitepackage_lawyerfilter"]')) {
      new LawyerFilter(container);
    }
    for (const container of document.querySelectorAll<HTMLElement>('[data-ctype="sitepackage_lawyerlist"]')) {
      new LawyerList(container);
    }
    for (const container of document.querySelectorAll<HTMLElement>('[data-ctype="sitepackage_lawyerpicker"]')) {
      new LawyerPicker(container);
    }
    for (const container of document.querySelectorAll<HTMLElement>('[data-ctype="sitepackage_lawyerquotes"]')) {
      new LawyerQuotes(container);
    }

    // News
    for (const container of document.querySelectorAll<HTMLElement>('[data-ctype="sitepackage_newsfilter"]')) {
      new NewsFilter(container);
    }
    for (const container of document.querySelectorAll<HTMLElement>('[data-ctype="sitepackage_newslatest"]')) {
      new NewsLatest(container);
    }
    for (const container of document.querySelectorAll<HTMLElement>('[data-ctype="sitepackage_newslist"]')) {
      new NewsList(container);
    }
    for (const container of document.querySelectorAll<HTMLElement>('[data-ctype="sitepackage_newspicker"]')) {
      new NewsPicker(container);
    }

    // Publication
    for (const container of document.querySelectorAll<HTMLElement>('[data-ctype="sitepackage_publicationfilter"]')) {
      new PublicationFilter(container);
    }
    for (const container of document.querySelectorAll<HTMLElement>('[data-ctype="sitepackage_publicationlist"]')) {
      new PublicationList(container);
    }
    for (const container of document.querySelectorAll<HTMLElement>('[data-ctype="sitepackage_publicationpicker"]')) {
      new PublicationPicker(container);
    }
  }
}
